import React from 'react'
import { Box, CircularProgress, Modal, styled, Typography, Button, DialogActions } from '@mui/material'
import { BorderButtonPurple, CustomButton } from '../buttons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons'
import lightTheme from '../../styles/theme/lightTheme'
import '../../styles/components/_box.scss'
import { useTranslation } from 'react-i18next'

const ModalContainer = styled(Box)(({ theme }) => ({
  width: 'calc(100% - 32px)',
  maxWidth: 620,
  maxHeight: 'calc(100dvh - 32px)',
  height: 'auto',
  backgroundColor: '#FFFFFF',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  padding: '24px 20px',
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  rowGap: '24px',
  overflow: 'auto',
  boxSizing: 'border-box',

  '&:focus-visible': {
    outline: 'none',
  },

  [theme.breakpoints.up('sm')]: {
    width: 620,
    height: 450,
    maxHeight: 'none',
    padding: '30px 85px',
    rowGap: '50px',
    borderRadius: 0,
  },
}))

const IconBox = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  '.iconExclam': {
    color: lightTheme.palette.primary.main,
    fontSize: 56,
  },

  [theme.breakpoints.up('sm')]: {
    width: 85,
    height: 85,

    '.iconExclam': {
      fontSize: 80,
    },
  },
}))

const DeleteGeneralModal = ({ open, onClose, onConfirm, loading }) => {
  const { t } = useTranslation()
  return (
    <Modal
      open={open}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(51, 51, 51, 0.65)',
        },
      }}
    >
      <ModalContainer>
        <IconBox>
          <FontAwesomeIcon
            icon={faCircleExclamation}
            className={'iconExclam'}
          />
        </IconBox>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: { xs: '12px', sm: '20px' },
            textAlign: 'center',
            width: '100%',
            px: { xs: 0, sm: 0 },
          }}
        >
          <Typography variant="subtitle2" sx={{ lineHeight: 1.4 }}>
            {t('box.deleteTitle')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ lineHeight: 1.6, wordBreak: 'break-word' }}
          >
            {t('box.deleteText')}
          </Typography>
        </Box>
        <DialogActions
          sx={{
            width: '100%',
            px: { xs: 0, sm: 3 },
            pb: { xs: 0, sm: 3 },
            pt: 0,
            m: 0,
            gap: 1.5,
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center',
            flexShrink: 0,
            '& > button': {
              width: { xs: '100%', sm: 'auto' },
              m: 0,
            },
          }}
        >
          {loading ? (
            <CircularProgress />
          ) : (
            <>
              <Button
                onClick={onClose}
                variant="outlined"
                sx={{
                  borderColor: '#e5e7eb',
                  color: '#6b7280',
                  py: { xs: 1.25, sm: 1 },
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    backgroundColor: '#f9fafb',
                  },
                }}
              >
                {t('button.deleteNo')}
              </Button>
              <Button
                onClick={onConfirm}
                variant="contained"
                sx={{
                  background: 'linear-gradient(90deg, #dc3545 0%, #c82333 100%)',
                  px: 4,
                  py: { xs: 1.25, sm: 1 },
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    background: 'linear-gradient(90deg, #c82333 0%, #a71e2a 100%)',
                  },
                }}
              >
                {t('button.deleteYes')}
              </Button>
            </>
          )}
        </DialogActions>
      </ModalContainer>
    </Modal>
  )
}

export default DeleteGeneralModal
